import React, { useState, useEffect } from "react";
import { getCgv, createCgv } from "services/myCgv.services";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { FileUploaderInput, FormButton, NumberInput } from "reusableComponents";
import { useSelector } from "react-redux";
import { selectAuthToken } from "store/auth/selectors";
import { useLanguage } from "context";

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB

const MyTermsAndConditionsPage = () => {
  const accessToken = useSelector(selectAuthToken);
  const { t } = useTranslation("global");
  const { language } = useLanguage();

  const [cgvModel, setCgvModel] = useState({
    condition_days_1: "",
    condition_days_2: "",
    condition_days_3: "",
    condition_taux_1: "",
    condition_taux_2: "",
    condition_taux_3: "",
  });

  const [cgvFile, setCgvFile] = useState(null);          // NEW uploaded file
  const [existingFile, setExistingFile] = useState(null); // Server file

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  /* ---------------- FETCH ---------------- */

  useEffect(() => {
    const fetchCgvData = async () => {
      try {
        setLoading(true);
        const data = await getCgv(accessToken, language);

        setCgvModel({
          condition_days_1: data.condition_days_1 ?? "",
          condition_days_2: data.condition_days_2 ?? "",
          condition_days_3: data.condition_days_3 ?? "",
          condition_taux_1: data.condition_taux_1 ?? "",
          condition_taux_2: data.condition_taux_2 ?? "",
          condition_taux_3: data.condition_taux_3 ?? "",
        });

        setExistingFile(data.document || null);
      } catch {
        setErrorMessage("Failed to load CGV data.");
      } finally {
        setLoading(false);
      }
    };

    fetchCgvData();
  }, [accessToken, language]);

  /* ---------------- INPUTS ---------------- */

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCgvModel((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setErrorMessage("");
    setSuccessMessage("");

    if (!file) {
      setCgvFile(null);
      return;
    }

    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setErrorMessage("The field document should be a PDF file.");
      setCgvFile(null);
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setErrorMessage("File size should not exceed 1MB.");
      setCgvFile(null);
      return;
    }

    setCgvFile(file);
    setExistingFile(null); // override old file
  };

  /* ---------------- VALIDATION ---------------- */

  const validateForm = () => {
    const valuesFilled = Object.values(cgvModel).every(
      (v) => v !== "" && v !== null
    );

    if (!valuesFilled) {
      setErrorMessage("Please fill in all fields.");
      return false;
    }

    if (!cgvFile && !existingFile) {
      setErrorMessage("Please upload a document.");
      return false;
    }

    return true;
  };

  /* ---------------- SUBMIT ---------------- */

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        ...cgvModel,
        document: cgvFile ?? undefined,
      };

      const response = await createCgv(payload, accessToken);
      setSuccessMessage(response.message);
    } catch {
      setErrorMessage("Failed to update CGV. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- RENDER ---------------- */

  return (
    <form
      className={`${styles.myTermsAndConditionsForm} ${
        loading && styles.loading
      }`}
      onSubmit={handleSubmit}
      noValidate
    >
      <div className={styles.explanationSection}>
        <h2 className={styles.dashboardTitle}>
          {t("dashboard.cgv.title")}
        </h2>
        <p dangerouslySetInnerHTML={{ __html: t("dashboard.cgv.explain") }} />
      </div>

      <h2 className={styles.dashboardTitle}>
        "{t("dashboard.cgv.cancel.title")}
      </h2>

      <div className={`${styles.formSection} ${styles.cancelConditions}`}>
        <p
          dangerouslySetInnerHTML={{
            __html: t("dashboard.cgv.cancel.explain"),
          }}
        />

        {[1, 2, 3].map((index) => (
          <div key={index} className={styles.condition}>
            <p className={styles.conditionTitle}>
              {`Condition n°${index}: `}
            </p>

            <NumberInput
              label={`${t("dashboard.cgv.delai")} *`}
              name={`condition_days_${index}`}
              value={cgvModel[`condition_days_${index}`]}
              min={0}
              onChange={handleInputChange}
              labelAbove
              addonText={t("dashboard.cgv.days")}
            />

            <NumberInput
              label={`${t("dashboard.cgv.pourcentage")} *`}
              name={`condition_taux_${index}`}
              value={cgvModel[`condition_taux_${index}`]}
              min={0}
              max={100}
              labelAbove
              onChange={handleInputChange}
              addonText="%"
            />
          </div>
        ))}
      </div>

      <h3 className={styles.dashboardTitle}>
        {t("dashboard.cgv.download.title")}
      </h3>

      <div className={styles.formSection}>
        <p>{t("dashboard.cgv.download.explain")}</p>

        <FileUploaderInput
          label={t("dashboard.cgv.download.file")}
          onChange={handleFileChange}
          file={cgvFile}
          existingFile={existingFile}
        />
      </div>

      <div className={styles.messages}>
        {errorMessage && (
          <div className={styles.errorMessage}>{errorMessage}</div>
        )}
        {successMessage && !errorMessage && (
          <div className={styles.successMessage}>{successMessage}!</div>
        )}
      </div>

      <div className={styles.formActions}>
        <FormButton
          label={t("buttons.save")}
          type="submit"
          className={styles.submitButton}
          disabled={loading || !!errorMessage}
        />
      </div>
    </form>
  );
};

export default MyTermsAndConditionsPage;
